/* cat-trainer.js — vanilla progressive enhancement for the AI Cat Trainer page.
 * Replaces the framework runtime from the React prototype:
 *   1. the interactive trainer chat (CatTrainerChat) — profile switch, topic → typed reply
 * No dependencies. Self-contained. Safe to load with `defer`.
 *
 * The page renders a faithful static snapshot server-side; this script enhances it.
 * If the script never runs, the static chat shell + FAQ questions are still visible.
 */
(function () {
  'use strict';

  // <html lang> comes from request.locale.iso_code, so /de/ pages report "de".
  var SN_DE = typeof document !== 'undefined' && document.documentElement
    ? /^de/i.test(document.documentElement.getAttribute('lang') || '')
    : false;
  var T = function (en, de) { return SN_DE ? de : en; };

  // ---- Chat data (ported verbatim from CatTrainerChat.jsx) -----------------
  var PROFILES = [
    { id: 'bandit', name: 'Bandit', meta: '8 mo · bold kitten', bg: '#CFFF90',
      lead: 'all that kitten energy is your ally here - keep sessions short, high-reward and a bit silly, and stop before he tips into the zoomies' },
    { id: 'luna', name: 'Luna', meta: '4 yr · shy rescue', bg: '#FFDDBF',
      lead: 'go entirely at her pace - let her opt in, never corner her, and reward the smallest brave step like it’s a triumph' },
    { id: 'smudge', name: 'Smudge', meta: '3 yr · feral-turned-house', bg: '#E6FFC6',
      lead: 'with a feral background, trust does the heavy lifting - work at a distance first and let curiosity, not pressure, pull him in' },
  ];

  var TOPICS = [
    { id: 'litter', label: 'Litter training', q: 'How do I get litter training right?',
      a: function (p) { return [
        'Good news with ' + p.name + ': most cats take to a box fast with the right setup, and ' + p.lead + '.',
        'Start with one more box than you have cats, in a quiet, low-traffic spot - not next to the food. Use a large uncovered tray and an unscented clumping litter to begin with. Scoop daily; cats are fussier about a dirty box than we are.',
        'If ' + p.name + ' suddenly stops using it, that’s information, not defiance - a change in litter, a dirty tray, or stress is usually behind it, and it’s worth ruling out a health issue too.',
      ]; } },
    { id: 'toilet', label: 'Toilet training', q: 'Should I try toilet training?',
      a: function (p) { return [
        'Honest answer for ' + p.name + ': it can work for some cats with a training kit and a lot of patience - but it isn’t for every cat, and there’s no shame in deciding it’s not for yours.',
        'If you try, go in tiny stages over weeks, never rush a level, and stop if ' + p.name + ' looks stressed or starts holding it in. A litter tray they reliably use beats a loo they resent.',
      ]; } },
    { id: 'harness', label: 'Harness training', q: 'How do I harness train my cat?',
      a: function (p) { return [
        'Harness training is all about patience, and with ' + p.name + ' especially - ' + p.lead + '.',
        'Let the harness live near their bed for a few days, then drape it on for seconds with a treat, building to a proper fit indoors. Only once they’re happily pottering about in it do you clip on a lead - and only then think about the garden.',
        'If they do the classic flop-and-freeze, you’ve gone a step too fast. Drop back a level and rebuild.',
      ]; } },
    { id: 'clicker', label: 'Clicker training', q: 'Does clicker training actually work?',
      a: function (p) { return [
        'Yes - and cats pick it up faster than people expect. ' + p.name + ' will too: ' + p.lead + '.',
        'Click the instant they do the thing you like, then treat within a second or two so the click means “treat’s coming.” Keep it to 2–3 minutes. Start with an easy win like a nose-touch to your finger, then build to sit, recall, even high-fives.',
      ]; } },
    { id: 'scratch', label: 'Scratching', q: 'How do I stop the sofa-scratching?',
      a: function (p) { return [
        'Scratching isn’t naughtiness - it’s a need. The trick with ' + p.name + ' is redirection, not punishment.',
        'Put a tall, sturdy post right next to the spot they’re targeting (cats scratch to mark and stretch, so location matters). Reward every use of the post, make the sofa boring with a temporary cover or double-sided tape, and keep claws trimmed.',
      ]; } },
    { id: 'night', label: 'Night-time zoomies', q: 'How do I stop the 3am zoomies?',
      a: function (p) { return [
        'The 3am gallop is a hungry, under-stimulated cat on hunting time. For ' + p.name + ', ' + p.lead + '.',
        'Move the big play session to just before your bedtime - a proper wand-toy hunt that ends in a “catch,” followed by their last meal. A hunt-eat-groom-sleep cycle is exactly how a cat is wired to wind down. Try very hard not to get up and feed them at 3am, or you’ve just trained the alarm clock.',
      ]; } },
  ];

  // ---- German overrides ----------------------------------------------------
  if (SN_DE) {
    PROFILES = [
      { id: 'bandit', name: 'Bandit', meta: '8 Mon. · forsches Kitten', bg: '#CFFF90',
        lead: 'die ganze Kitten-Energie ist hier Ihr Verbündeter - halten Sie die Einheiten kurz, belohnen Sie großzügig, seien Sie ruhig albern, und hören Sie auf, bevor er in die wilde Phase kippt' },
      { id: 'luna', name: 'Luna', meta: '4 J. · scheue Tierschutzkatze', bg: '#FFDDBF',
        lead: 'gehen Sie ganz in ihrem Tempo - lassen Sie sie selbst entscheiden, drängen Sie sie nie in die Enge, und feiern Sie den kleinsten mutigen Schritt wie einen Triumph' },
      { id: 'smudge', name: 'Smudge', meta: '3 J. · ehemals wild lebend', bg: '#E6FFC6',
        lead: 'bei einer Katze mit wildem Vorleben trägt Vertrauen die Hauptlast - arbeiten Sie zuerst auf Distanz und lassen Sie Neugier statt Druck den Weg machen' },
    ];
    TOPICS = [
      { id: 'litter', label: 'Katzenklo & stubenrein', q: 'Wie wird meine Katze zuverlässig stubenrein?',
        a: function (p) { return [
          'Gute Nachricht bei ' + p.name + ': Die meisten Katzen nehmen das Klo mit dem richtigen Aufbau schnell an, und ' + p.lead + '.',
          'Stellen Sie ein Klo mehr auf, als Sie Katzen haben, an einem ruhigen Ort mit wenig Durchgangsverkehr - nicht neben dem Futter. Beginnen Sie mit einer großen, offenen Schale und einer unparfümierten Klumpstreu. Täglich sauber machen: Katzen sind bei einem schmutzigen Klo empfindlicher als wir.',
          'Pinkelt eine Katze plötzlich in die Wohnung oder auf den Teppich, obwohl sie stubenrein war, ist das fast nie Trotz. Lassen Sie zuerst eine Blasenentzündung tierärztlich ausschließen, und schauen Sie dann auf Veränderungen: neues Klo, neue Streu, Umzug, Besuch, ein zweites Tier.',
        ]; } },
      { id: 'toilet', label: 'Toilettentraining', q: 'Soll ich Toilettentraining versuchen?',
        a: function (p) { return [
          'Ehrliche Antwort für ' + p.name + ': Mit einem Aufsatz-Set und viel Geduld klappt es bei manchen Katzen - aber längst nicht bei allen, und es ist keine Schande, sich dagegen zu entscheiden.',
          'Wenn Sie es versuchen, gehen Sie über Wochen in winzigen Stufen vor, überspringen Sie nie eine Stufe, und hören Sie auf, sobald ' + p.name + ' gestresst wirkt oder anfängt einzuhalten. Ein Katzenklo, das zuverlässig benutzt wird, ist besser als eine Toilette, die Ihre Katze meidet.',
        ]; } },
      { id: 'harness', label: 'Geschirrtraining', q: 'Wie gewöhne ich meine Katze an ein Geschirr?',
        a: function (p) { return [
          'Geschirrtraining ist reine Geduldssache, und bei ' + p.name + ' ganz besonders - ' + p.lead + '.',
          'Lassen Sie das Geschirr ein paar Tage neben dem Schlafplatz liegen, legen Sie es dann sekundenweise mit einem Leckerli auf und steigern Sie bis zum richtigen Sitz in der Wohnung. Erst wenn Ihre Katze zufrieden damit herumläuft, kommt die Leine dazu - und erst danach denken Sie an den Garten.',
          'Kommt der klassische Flach-Hinlegen-und-Erstarren-Moment, war ein Schritt zu groß. Gehen Sie eine Stufe zurück und bauen Sie neu auf.',
        ]; } },
      { id: 'clicker', label: 'Clickertraining', q: 'Funktioniert Clickertraining bei Katzen wirklich?',
        a: function (p) { return [
          'Ja - und Katzen begreifen es schneller, als die meisten erwarten. ' + p.name + ' auch: ' + p.lead + '.',
          'Clicken Sie in dem Moment, in dem sie das Gewünschte tut, und geben Sie ein bis zwei Sekunden später das Leckerli, damit der Click „gleich kommt etwas“ bedeutet. Zwei bis drei Minuten reichen. Starten Sie mit etwas Leichtem wie einem Nasenstupser an Ihren Finger und bauen Sie dann Sitz, Rückruf oder sogar High-Five auf.',
        ]; } },
      { id: 'scratch', label: 'Kratzen', q: 'Wie halte ich meine Katze vom Sofa ab?',
        a: function (p) { return [
          'Kratzen ist keine Unart, sondern ein Bedürfnis. Bei ' + p.name + ' geht es ums Umlenken, nicht ums Bestrafen.',
          'Stellen Sie einen hohen, standfesten Kratzbaum direkt an die Stelle, die sie sich ausgesucht hat - Katzen kratzen zum Markieren und Strecken, der Ort ist also entscheidend. Belohnen Sie jede Nutzung, machen Sie das Sofa vorübergehend uninteressant (Überwurf oder doppelseitiges Klebeband) und halten Sie die Krallen kurz.',
        ]; } },
      { id: 'night', label: 'Nächtliche Wildheit', q: 'Was tun gegen die wilde Phase um drei Uhr nachts?',
        a: function (p) { return [
          'Der Galopp um drei Uhr früh ist eine hungrige, unterforderte Katze zur Jagdzeit. Bei ' + p.name + ': ' + p.lead + '.',
          'Verlegen Sie die große Spieleinheit direkt vor Ihre Schlafenszeit - eine richtige Jagd mit der Angel, die in einem „Fang“ endet, danach die letzte Mahlzeit. Jagen, fressen, putzen, schlafen: genau so ist eine Katze zum Herunterfahren gebaut. Und stehen Sie nachts möglichst nicht zum Füttern auf, sonst haben Sie den Wecker antrainiert.',
        ]; } },
    ];
  }

  function greeting(p) {
    return [{ from: 'trainer', paras: [
      T('Hi! I’m your cat trainer. I’ve got ', 'Hallo! Ich bin Ihre Katzentrainerin. Ich habe das Profil von ') + p.name + T('’s profile open - ', ' offen - ') +
      p.meta.split('·')[1].trim() + T('. Ask me anything, or tap a topic below.', '. Fragen Sie mich alles oder tippen Sie unten ein Thema an.'),
    ] }];
  }

  // ---- SVG fragments (ported from cat-training-illustrations.jsx) -----------
  function orb(size) {
    var face = '#224031', ring = '#CFFF90';
    return '<svg width="' + size + '" height="' + size + '" viewBox="0 0 120 120" fill="none" aria-hidden="true">' +
      '<path d="M30 30L26 8l22 12Z" fill="' + face + '"></path>' +
      '<path d="M90 30L94 8 72 20Z" fill="' + face + '"></path>' +
      '<path d="M32 26l-2-11 11 7Z" fill="' + ring + '" opacity="0.8"></path>' +
      '<path d="M88 26l2-11-11 7Z" fill="' + ring + '" opacity="0.8"></path>' +
      '<circle cx="60" cy="62" r="50" fill="' + face + '"></circle>' +
      '<circle cx="60" cy="62" r="50" fill="none" stroke="' + ring + '" stroke-width="3" stroke-dasharray="6 9" opacity="0.7"></circle>' +
      '<ellipse cx="46" cy="56" rx="6.5" ry="8" fill="' + ring + '"></ellipse>' +
      '<ellipse cx="74" cy="56" rx="6.5" ry="8" fill="' + ring + '"></ellipse>' +
      '<circle cx="46" cy="58" r="2.6" fill="' + face + '"></circle>' +
      '<circle cx="74" cy="58" r="2.6" fill="' + face + '"></circle>' +
      '<path d="M56 70l8 0-4 4Z" fill="' + ring + '"></path>' +
      '<path d="M60 74c0 5 8 6 12 2M60 74c0 5-8 6-12 2" stroke="' + ring + '" stroke-width="3.5" stroke-linecap="round" fill="none"></path>' +
      '<g stroke="' + ring + '" stroke-width="2.4" stroke-linecap="round" opacity="0.85"><path d="M30 66h14M30 74h14M90 66H76M90 74H76"></path></g>' +
      '<path d="M96 26l2 6 6 2-6 2-2 6-2-6-6-2 6-2Z" fill="' + ring + '"></path></svg>';
  }
  function avatar(size, bg) {
    var ink = '#224031';
    return '<svg width="' + size + '" height="' + size + '" viewBox="0 0 48 48" fill="none" aria-hidden="true">' +
      '<circle cx="24" cy="24" r="24" fill="' + bg + '"></circle>' +
      '<path d="M14 18l-2-8 9 5Z" fill="' + ink + '"></path>' +
      '<path d="M34 18l2-8-9 5Z" fill="' + ink + '"></path>' +
      '<circle cx="24" cy="26" r="12" fill="' + ink + '"></circle>' +
      '<circle cx="20" cy="24" r="2" fill="' + bg + '"></circle>' +
      '<circle cx="28" cy="24" r="2" fill="' + bg + '"></circle>' +
      '<path d="M22 29h4l-2 2Z" fill="' + bg + '"></path>' +
      '<g stroke="' + bg + '" stroke-width="1.3" stroke-linecap="round"><path d="M14 27h6M28 27h6M14 30h6M28 30h6"></path></g></svg>';
  }
  function icon(name, size, color) {
    var paths = {
      arrow: '<path d="M5 12h14M13 6l6 6-6 6"></path>',
      sparkle: '<path d="M12 3l1.8 4.9L18.7 9.7l-4.9 1.8L12 16.4l-1.8-4.9L5.3 9.7l4.9-1.8L12 3Z"></path>',
    };
    return '<svg width="' + size + '" height="' + size + '" viewBox="0 0 24 24" fill="none" stroke="' + color +
      '" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' + paths[name] + '</svg>';
  }

  // ---- Tiny DOM helper ------------------------------------------------------
  function h(tag, opts, kids) {
    var e = document.createElement(tag);
    opts = opts || {};
    if (opts.css) e.style.cssText = opts.css;
    if (opts.cls) e.className = opts.cls;
    if (opts.html != null) e.innerHTML = opts.html;
    if (opts.on) for (var ev in opts.on) e.addEventListener(ev, opts.on[ev]);
    if (opts.attrs) for (var a in opts.attrs) e.setAttribute(a, opts.attrs[a]);
    (kids || []).forEach(function (c) {
      if (c == null) return;
      e.appendChild(typeof c === 'string' ? document.createTextNode(c) : c);
    });
    return e;
  }

  // ---- Chat widget ----------------------------------------------------------
  function initChat(root) {
    var state = { profileId: 'bandit', messages: greeting(PROFILES[0]), typing: false, asked: [] };
    var typingTimer = null;
    var profile = function () { return PROFILES.filter(function (p) { return p.id === state.profileId; })[0]; };

    function bubble(msg, animate) {
      var isUser = msg.from === 'user';
      var inner = h('div', {
        css: 'max-width:78%;border-radius:18px;padding:12px 16px;font-size:14.5px;line-height:1.55;' +
          'background:' + (isUser ? 'var(--sn-forest)' : '#fff') + ';' +
          'color:' + (isUser ? 'var(--sn-cream)' : 'var(--sn-ink)') + ';' +
          'border:' + (isUser ? 'none' : '1px solid var(--sn-line)') + ';' +
          'border-bottom-right-radius:' + (isUser ? '5px' : '18px') + ';' +
          'border-bottom-left-radius:' + (isUser ? '18px' : '5px') + ';' +
          'display:flex;flex-direction:column;gap:9px',
      }, msg.typing
        ? [h('span', { css: 'display:inline-flex;gap:5px;color:var(--sn-ink-60);padding:2px 0',
            html: '<span class="sn-dot"></span><span class="sn-dot"></span><span class="sn-dot"></span>' })]
        : msg.paras.map(function (t) { return h('span', {}, [t]); }));

      return h('div', {
        cls: animate ? 'sn-msg-in' : '',
        css: 'display:flex;justify-content:' + (isUser ? 'flex-end' : 'flex-start') + ';gap:10px',
      }, [
        isUser ? null : h('div', { css: 'flex-shrink:0;margin-top:2px', html: orb(30) }),
        inner,
      ]);
    }

    function renderMessages(scroll) {
      var box = root.querySelector('[data-ct-messages]');
      box.innerHTML = '';
      var items = state.messages.slice();
      if (state.typing) items.push({ from: 'trainer', typing: true });
      items.forEach(function (m, i) { box.appendChild(bubble(m, i === items.length - 1)); });
      if (scroll !== false) box.scrollTop = box.scrollHeight;
    }

    function ask(topic) {
      if (state.typing) return;
      state.messages.push({ from: 'user', paras: [topic.q] });
      if (state.asked.indexOf(topic.id) === -1) state.asked.push(topic.id);
      state.typing = true;
      syncChips();
      renderMessages();
      clearTimeout(typingTimer);
      typingTimer = setTimeout(function () {
        state.messages.push({ from: 'trainer', paras: topic.a(profile()) });
        state.typing = false;
        syncChips();
        renderMessages();
      }, 850);
    }

    function switchProfile(id) {
      if (id === state.profileId) return;
      clearTimeout(typingTimer);
      state.typing = false;
      state.asked = [];
      state.profileId = id;
      state.messages = greeting(PROFILES.filter(function (p) { return p.id === id; })[0]);
      syncProfile();
      syncChips();
      renderMessages();
    }

    function syncChips() {
      root.querySelectorAll('[data-ct-topic]').forEach(function (btn) {
        var id = btn.getAttribute('data-ct-topic');
        btn.style.background = state.asked.indexOf(id) !== -1 ? 'var(--sn-lime-soft)' : '#fff';
        btn.style.opacity = state.typing ? '0.5' : '1';
        btn.style.cursor = state.typing ? 'default' : 'pointer';
        btn.disabled = state.typing;
      });
    }

    function syncProfile() {
      var p = profile();
      root.querySelectorAll('[data-ct-profile]').forEach(function (btn) {
        var active = btn.getAttribute('data-ct-profile') === state.profileId;
        btn.style.border = active ? '2px solid var(--sn-forest)' : '2px solid var(--sn-line)';
        btn.style.background = active ? 'var(--sn-lime-soft)' : '#fff';
        btn.style.boxShadow = active ? '0 0 0 4px rgba(207,255,144,0.35)' : 'none';
      });
      var lbl = root.querySelector('[data-ct-training]');
      if (lbl) lbl.textContent = 'Training ' + p.name;
      var ph = root.querySelector('[data-ct-placeholder]');
      if (ph) ph.textContent = 'Ask anything about training ' + p.name + '…';
      var build = root.querySelector('[data-ct-build]');
      if (build) build.textContent = 'Build ' + p.name + '’s profile';
    }

    // Build the interactive widget (replaces the static SSR snapshot).
    var p0 = profile();
    var header = h('div', { css: 'display:flex;align-items:center;gap:12px;padding:16px 22px;border-bottom:1px solid var(--sn-line)' }, [
      h('div', { css: 'flex-shrink:0', html: orb(40) }),
      h('div', { css: 'flex:1' }, [
        h('div', { css: 'font-family:var(--sn-display);font-weight:700;font-size:16px' }, ['Cat Trainer']),
        h('div', { css: 'font-size:12.5px;color:var(--sn-ink-60);display:flex;align-items:center;gap:6px' }, [
          h('span', { css: 'width:7px;height:7px;border-radius:999px;background:#5B8C3E' }),
          h('span', { attrs: { 'data-ct-training': '' } }, ['Training ' + p0.name]),
        ]),
      ]),
      h('span', { cls: 'sn-eyebrow', css: 'color:var(--sn-forest);font-size:11px', html: icon('sparkle', 14, 'var(--sn-forest)') + ' AI' }),
    ]);

    var messages = h('div', { attrs: { 'data-ct-messages': '' }, css: 'flex:1;overflow-y:auto;padding:22px;display:flex;flex-direction:column;gap:14px' });

    var chips = h('div', { css: 'padding:12px 16px;border-top:1px solid var(--sn-line);display:flex;flex-wrap:wrap;gap:8px;background:#fff' },
      TOPICS.map(function (t) {
        return h('button', {
          attrs: { 'data-ct-topic': t.id },
          css: 'cursor:pointer;border-radius:999px;padding:8px 14px;font-size:13px;font-weight:600;font-family:var(--sn-display);border:1.5px solid var(--sn-line);background:#fff;color:var(--sn-forest);opacity:1;display:inline-flex;align-items:center;gap:6px',
          on: { click: function () { ask(t); } },
        }, [t.label]);
      }));

    var inputRow = h('div', { css: 'padding:14px 16px;border-top:1px solid var(--sn-line);display:flex;gap:10px;align-items:center;background:#fff' }, [
      h('div', { attrs: { 'data-ct-placeholder': '' }, css: 'flex:1;background:var(--sn-cream);border-radius:999px;padding:12px 18px;font-size:14px;color:var(--sn-ink-60);border:1px solid var(--sn-line)' }, ['Ask anything about training ' + p0.name + '…']),
      h('button', { css: 'border:none;cursor:pointer;width:44px;height:44px;border-radius:999px;background:var(--sn-forest);display:inline-flex;align-items:center;justify-content:center;flex-shrink:0', html: icon('arrow', 20, 'var(--sn-cream)') }),
    ]);

    var leftCol = h('div', { css: 'display:flex;flex-direction:column;height:600px;border-right:1px solid var(--sn-line)' }, [header, messages, chips, inputRow]);

    var chipsPanel = h('div', { css: 'display:flex;flex-direction:column;gap:10px' },
      PROFILES.map(function (p) {
        var active = p.id === state.profileId;
        return h('button', {
          attrs: { 'data-ct-profile': p.id },
          css: 'cursor:pointer;text-align:left;display:flex;align-items:center;gap:12px;border-radius:16px;padding:12px 14px;' +
            'border:' + (active ? '2px solid var(--sn-forest)' : '2px solid var(--sn-line)') + ';' +
            'background:' + (active ? 'var(--sn-lime-soft)' : '#fff') + ';' +
            'box-shadow:' + (active ? '0 0 0 4px rgba(207,255,144,0.35)' : 'none'),
          on: { click: function () { switchProfile(p.id); } },
        }, [
          h('div', { css: 'flex-shrink:0', html: avatar(42, p.bg) }),
          h('div', {}, [
            h('div', { css: 'font-family:var(--sn-display);font-weight:700;font-size:15.5px' }, [p.name]),
            h('div', { css: 'font-size:12.5px;color:var(--sn-ink-60)' }, [p.meta]),
          ]),
        ]);
      }));

    var rightCol = h('div', { css: 'padding:26px 26px;background:#fff;display:flex;flex-direction:column' }, [
      h('h3', { css: 'font-size:19px;margin-bottom:6px' }, ['Who are we training?']),
      h('p', { css: 'font-size:13.5px;color:var(--sn-ink-60);line-height:1.5;margin-bottom:18px' }, ['A shy adult, a bold kitten and a feral-turned-house cat all need different handling. Pick one - watch the advice change.']),
      chipsPanel,
      h('div', { css: 'margin-top:auto;padding-top:22px' }, [
        h('div', { css: 'background:var(--sn-cream-2);border-radius:16px;padding:14px 16px;font-size:13px;color:var(--sn-ink-60);line-height:1.5' }, ['Your real trainer uses your cat’s age, breed and history from their profile.']),
        h('a', { cls: 'sn-btn sn-btn--primary', css: 'width:100%;justify-content:center;margin-top:12px' }, [
          h('span', { attrs: { 'data-ct-build': '' } }, ['Build ' + p0.name + '’s profile']),
          h('span', { css: 'display:inline-flex', html: icon('arrow', 18, 'currentColor') }),
        ]),
      ]),
    ]);

    var grid = h('div', { css: 'display:grid;grid-template-columns:1.5fr 1fr;border-radius:28px;overflow:hidden;border:1px solid var(--sn-line);box-shadow:0 30px 70px -45px rgba(28,25,23,0.5);background:#fff' }, [leftCol, rightCol]);

    root.innerHTML = '';
    root.appendChild(grid);
    renderMessages(false);
  }

  // The FAQ accordion is handled by the shared converted/_assets/sn-accordion.js.
  // Answers are rendered into the static body.html (for SEO/crawlability), so this
  // file only owns the chat widget.

  // ---- Boot -----------------------------------------------------------------
  function boot() {
    var page = document.getElementById('sn-cat-trainer');
    if (!page) return;
    var chat = page.querySelector('[data-sn-widget="CatTrainerChat"]');
    if (chat) initChat(chat);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
